import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as ChartTooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import apiClient from "../../api/client";

interface DailyPoint {
  date: string;
  calls: number;
  revenue: number;
  cost: number;
  margin: number;
}

interface Totals {
  calls: number;
  revenue: number;
  cost: number;
  margin: number;
}

interface TopRow {
  destination?: string;
  account?: string;
  call_count: number;
  total_revenue: number;
}

interface InvoiceRow {
  id: string;
  invoice_number: string;
  period_start: string;
  period_end: string;
  total_amount: number;
  currency: string;
  status: string;
  due_date: string;
}

const money = (v: number) => `$${(v ?? 0).toFixed(2)}`;

function SummaryCard({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <Card>
      <CardContent>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h6" sx={{ color: color || "text.primary", fontWeight: 600 }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
}

function statusChip(status: string) {
  const map: Record<string, "success" | "warning" | "error" | "default" | "info"> = {
    paid: "success", sent: "info", draft: "default",
    overdue: "error", partial: "warning", cancelled: "default",
  };
  return <Chip label={status.toUpperCase()} color={(map[status] || "default") as any} size="small" />;
}

/**
 * Reseller reports (Wave 5.10): revenue vs cost vs margin over time,
 * top destinations/customers, and own invoices.
 */
export default function ResellerReportsPage() {
  const [days, setDays] = useState(30);
  const [series, setSeries] = useState<DailyPoint[]>([]);
  const [totals, setTotals] = useState<Totals>({ calls: 0, revenue: 0, cost: 0, margin: 0 });
  const [topDest, setTopDest] = useState<TopRow[]>([]);
  const [topCust, setTopCust] = useState<TopRow[]>([]);
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const start = new Date(Date.now() - days * 86400_000).toISOString();
      const end = new Date().toISOString();

      const [report, dest, cust, inv] = await Promise.all([
        apiClient.get(`/api/v1/resellers/me/reports/daily?days=${days}`),
        apiClient.get(`/api/v1/resellers/me/top-destinations?limit=5&start_date=${start}&end_date=${end}`),
        apiClient.get(`/api/v1/resellers/me/top-customers?limit=5&start_date=${start}&end_date=${end}`),
        apiClient.get("/api/v1/resellers/me/invoices?limit=50"),
      ]);

      setSeries(report.data.series || []);
      setTotals(report.data.totals || { calls: 0, revenue: 0, cost: 0, margin: 0 });
      setTopDest(dest.data || []);
      setTopCust(cust.data || []);
      setInvoices(inv.data.invoices || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  const marginPct = totals.revenue > 0 ? (totals.margin / totals.revenue) * 100 : 0;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Reports
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Revenue, cost and margin across your account and downstream customers.
          </Typography>
        </Box>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={days}
          onChange={(_, v) => v && setDays(v)}
        >
          <ToggleButton value={7}>7 days</ToggleButton>
          <ToggleButton value={30}>30 days</ToggleButton>
          <ToggleButton value={90}>90 days</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <SummaryCard label="Calls" value={String(totals.calls)} />
        </Grid>
        <Grid item xs={6} md={3}>
          <SummaryCard label="Revenue" value={money(totals.revenue)} />
        </Grid>
        <Grid item xs={6} md={3}>
          <SummaryCard label="Carrier cost" value={money(totals.cost)} />
        </Grid>
        <Grid item xs={6} md={3}>
          <SummaryCard
            label={`Margin (${marginPct.toFixed(1)}%)`}
            value={money(totals.margin)}
            color="success.main"
          />
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Daily revenue vs carrier cost ($)
        </Typography>
        <Box sx={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <BarChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tickFormatter={(d: string) => (d || "").slice(5)} fontSize={12} />
              <YAxis fontSize={12} />
              <ChartTooltip
                formatter={(value: any) => money(Number(value))}
                labelFormatter={(l: any) => `Date: ${l}`}
              />
              <Legend />
              <Bar dataKey="revenue" name="Revenue" fill="#2e7d32" radius={[3, 3, 0, 0]} />
              <Bar dataKey="cost" name="Carrier cost" fill="#ef6c00" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
        {series.length === 0 && !loading && (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: -22, mb: 18 }}>
            No traffic in this period yet — charts fill in as calls complete.
          </Typography>
        )}
      </Paper>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, pt: 1 }}>
          <Tab label="Top destinations" />
          <Tab label="Top customers" />
        </Tabs>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{tab === 0 ? "Destination" : "Customer account"}</TableCell>
                <TableCell align="right">Calls</TableCell>
                <TableCell align="right">Revenue</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(tab === 0 ? topDest : topCust).map((row, i) => (
                <TableRow key={i} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                      {row.destination ?? row.account ?? "—"}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{row.call_count}</TableCell>
                  <TableCell align="right">{money(row.total_revenue)}</TableCell>
                </TableRow>
              ))}
              {(tab === 0 ? topDest : topCust).length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No data in this period.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Typography variant="h6" gutterBottom>
        Your invoices
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Invoice</TableCell>
              <TableCell>Period</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Due</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.map((inv) => (
              <TableRow key={inv.id} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {inv.invoice_number}
                  </Typography>
                </TableCell>
                <TableCell>
                  {new Date(inv.period_start).toLocaleDateString()} –{" "}
                  {new Date(inv.period_end).toLocaleDateString()}
                </TableCell>
                <TableCell align="right">{money(inv.total_amount)}</TableCell>
                <TableCell>{statusChip(inv.status)}</TableCell>
                <TableCell>{new Date(inv.due_date).toLocaleDateString()}</TableCell>
              </TableRow>
            ))}
            {invoices.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No invoices issued to your account yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
